import { Alert } from 'antd';
import type { ReactNode } from 'react';
import {
  AuditOutlined,
  BankOutlined,
  CameraOutlined,
  IdcardOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  CloudUploadOutlined,
  FileDoneOutlined,
  FormOutlined,
  LockOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { Task, TaskKind, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { StatusPill } from './StatusPill';
import { WhyWeAsk } from './WhyWeAsk';
import { UploadTask } from './tasks/UploadTask';
import { FormTask } from './tasks/FormTask';
import { AcknowledgeTask } from './tasks/AcknowledgeTask';
import { DeclarationTask } from './tasks/DeclarationTask';
import { PartiesTask } from './tasks/PartiesTask';
import { CaptureTask } from './tasks/CaptureTask';
import { OtpTask } from './tasks/OtpTask';
import { AadhaarOtpTask } from './tasks/AadhaarOtpTask';
import { IdentifierTask } from './tasks/IdentifierTask';
import { BankDetailsTask } from './tasks/BankDetailsTask';
import { erasedOr } from '@/lib/erased';

const KIND_ICON: Record<TaskKind, ReactNode> = {
  UPLOAD: <CloudUploadOutlined aria-hidden="true" />,
  FORM: <FormOutlined aria-hidden="true" />,
  ACKNOWLEDGE: <FileDoneOutlined aria-hidden="true" />,
  DECLARATION: <AuditOutlined aria-hidden="true" />,
  PARTIES: <TeamOutlined aria-hidden="true" />,
  CAPTURE: <CameraOutlined aria-hidden="true" />,
  OTP: <MobileOutlined aria-hidden="true" />,
  AADHAAR_OTP: <SafetyCertificateOutlined aria-hidden="true" />,
  IDENTIFIER: <IdcardOutlined aria-hidden="true" />,
  BANK_DETAILS: <BankOutlined aria-hidden="true" />,
};

/**
 * One task card: a plain-language title, the "why we ask" reason, a status pill, and the per-kind
 * action. `onStatus` reports the SERVER-returned status upward (optimistic-but-honest — we reflect
 * what the server confirmed, never a guessed state).
 */
export function TaskCard({
  task,
  onStatus,
  onRefresh,
  locked = false,
}: {
  task: Task;
  /** A sequential journey: an earlier step comes first. The server refuses it anyway (STEP_LOCKED). */
  locked?: boolean;
  onStatus: (taskId: string, status: TaskStatus) => void;
  /** Quietly re-read the task list (a DECLARATION whose text moved on asks for this). */
  onRefresh?: () => void;
}) {
  const { t } = useI18n();
  const done = task.status === 'ACCEPTED';
  const report = (status: TaskStatus) => onStatus(task.taskId, status);

  return (
    <section className={`dr-task${done ? ' dr-task--done' : ''}`} aria-label={erasedOr(task.title, t('common.erased'))}>
      <div className="dr-task__head">
        <span className="dr-task__kind">{KIND_ICON[task.kind]}</span>
        <div className="dr-task__titles">
          <h2 className="dr-task__title">{erasedOr(task.title, t('common.erased'))}</h2>
          {task.kind === 'UPLOAD' && task.evidenceKind && (
            <div className="dr-task__evidence">
              {t('upload.evidenceHint', { kind: task.evidenceKind })}
            </div>
          )}
        </div>
        {task.optional && task.status === 'PENDING' && (
          <span className="dr-task__optional" data-testid="task-optional">
            {t('tasks.optional')}
          </span>
        )}
        <StatusPill status={task.status} />
      </div>

      <WhyWeAsk text={task.whyItMatters} />
      {task.ui?.text?.help && <div className="dr-meta dr-task__help">{task.ui.text.help}</div>}

      {task.reopenNote && task.status === 'PENDING' && (
        <Alert
          type="warning"
          showIcon
          message={t('tasks.reopened')}
          description={task.reopenNote}
          data-testid="task-reopened"
          style={{ marginBottom: 12 }}
        />
      )}

      {locked && (
        <div className="dr-meta dr-task__locked" data-testid="task-locked">
          <LockOutlined aria-hidden="true" /> {t('tasks.locked')}
        </div>
      )}

      {!locked && task.kind === 'UPLOAD' && <UploadTask task={task} onStatus={report} />}
      {!locked && task.kind === 'FORM' && <FormTask task={task} onStatus={report} />}
      {!locked && task.kind === 'ACKNOWLEDGE' && <AcknowledgeTask task={task} onStatus={report} />}
      {!locked && task.kind === 'DECLARATION' && (
        <DeclarationTask task={task} onStatus={report} onRefresh={onRefresh} />
      )}
      {!locked && task.kind === 'PARTIES' && <PartiesTask task={task} onStatus={report} />}
      {!locked && task.kind === 'CAPTURE' && <CaptureTask task={task} onStatus={report} />}
      {!locked && task.kind === 'OTP' && <OtpTask task={task} onStatus={report} />}
      {!locked && task.kind === 'AADHAAR_OTP' && <AadhaarOtpTask task={task} onStatus={report} />}
      {!locked && task.kind === 'IDENTIFIER' && <IdentifierTask task={task} onStatus={report} />}
      {!locked && task.kind === 'BANK_DETAILS' && <BankDetailsTask task={task} onStatus={report} />}
    </section>
  );
}
